import { ClientWorkFlowStatus } from "@wingspanhq/payments/dist/interfaces";
import { WSQueryCache } from "@ws-react-query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../BulkActions";
import {
  QUERY_PAYABLES as QUERY_PAYABLES_NEW,
  QUERY_PAYABLES_SUMMARY
} from "../../../query/payables/keys";
import {
  QUERY_PAYABLE,
  QUERY_PAYABLES as QUERY_PAYABLES_LEGACY
} from "../../../query/payments/keys";
import { paymentsService } from "../../../services/payments";
import { Payable } from "../../../services/api/payments/payables";

const invalidateAfterMutation = (successItems: Payable[]) => {
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_NEW);
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_LEGACY);
  WSQueryCache.invalidateQueries(QUERY_PAYABLES_SUMMARY);
  successItems.forEach(payable => {
    if (payable.payableId) {
      WSQueryCache.invalidateQueries([QUERY_PAYABLE, payable.payableId]);
    }
  });
};

const processUnapprovePayables = (
  items: Payable[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> => {
  const bulkAction = bulkActionFactory<Payable>({
    onProgress,
    action: async item => {
      if (!item.payableId) {
        throw new Error("Payable is missing payableId");
      }
      if (item.status === "Paid") {
        throw new Error("Cannot unapprove paid payable");
      }
      return paymentsService.payable.update(item.payableId, {
        client: { workflowStatus: ClientWorkFlowStatus.Pending }
      });
    },
    onFinish: ({ successItems }) => {
      invalidateAfterMutation(successItems);
      onSuccess?.();
    }
  });

  return bulkAction.run(items).then(({ successItems, failedItems }) => ({
    total: items.length,
    processed: successItems.length + failedItems.length,
    successful: successItems.length,
    failed: failedItems.length
  }));
};

export const useBulkUnapprovePayables = () => {
  const runBulkAction = useForegroundBulkAction();

  return (items: Payable[], onSuccess?: () => void) =>
    runBulkAction({
      actionName: "Unapprove payables",
      itemCount: items.length,
      confirmDescription: "Unapprove payables from being paid",
      confirmLabel: "Unapprove",
      prepare: async () => items,
      process: async (data, onProgress) =>
        processUnapprovePayables(data, onProgress, onSuccess)
    });
};
